import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, Award } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function CertificateVerify() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/guided/certificate/verify/${certId}`)
      .then((r) => setData(r.data)).catch(() => setData({ valid: false })).finally(() => setLoading(false));
  }, [certId]);

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground bg-slate-50">Verifying certificate…</div>;

  if (!data?.valid) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="cert-verify-invalid">
        <div className="max-w-md w-full rounded-xl border bg-white p-8 text-center shadow-sm">
          <ShieldX className="h-12 w-12 text-red-500 mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-slate-900">Certificate not found</h1>
          <p className="mt-2 text-sm text-slate-500">We couldn't verify a certificate with ID <strong>{certId}</strong>. It may not have been issued yet.</p>
          <Link to="/"><Button variant="outline" className="mt-5">Go home</Button></Link>
        </div>
      </div>
    );
  }

  const c = data.certificate;
  const print = () => window.print();
  return (
    <div className="min-h-screen bg-slate-50 p-6 flex flex-col items-center" data-testid="cert-verify-valid">
      <div className="max-w-2xl w-full mt-8">
        <div className="flex items-center justify-center gap-2 text-green-600 mb-4" data-testid="cert-verify-badge">
          <ShieldCheck className="h-5 w-5" /> <span className="text-sm font-semibold uppercase tracking-widest">Verified authentic</span>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div id="cert-print" className="p-10 border-[6px] border-double border-[#0078d4] m-4 text-center">
            <div className="text-[11px] tracking-[0.3em] text-[#0078d4] font-semibold uppercase">{c.issuer}</div>
            <Award className="h-10 w-10 text-[#0078d4] mx-auto mt-4" />
            <div className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900">Certificate of Completion</div>
            <div className="mt-2 text-sm text-slate-500">This certifies that</div>
            <div className="mt-3 text-2xl font-bold text-[#0078d4]">{c.student_name}</div>
            <div className="mt-3 text-sm text-slate-600 max-w-md mx-auto">has successfully completed the <strong>{c.title}</strong>, demonstrating hands-on proficiency across all guided Azure engineering labs.</div>
            <div className="mt-3 text-sm text-slate-600 max-w-md mx-auto">has successfully completed the <strong>{c.title}</strong>, demonstrating hands-on proficiency across the Azure Cloud Academy{c.labs_completed ? ` (${c.labs_completed} hands-on labs)` : ""}.</div>
            {c.track_badges?.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                {c.track_badges.map((b) => <span key={b} className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">🏅 {b}</span>)}
              </div>
            )}
            {c.distinction && <div className="mt-3 inline-block text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-full">🏆 {c.distinction}</div>}
            <div className="mt-5 flex flex-wrap justify-center gap-1.5">
              {(c.skills || []).map((s) => <span key={s} className="text-[10px] uppercase tracking-wide bg-[#0078d4]/10 text-[#0078d4] px-2 py-0.5 rounded-full">{s}</span>)}
            </div>
            <div className="mt-6 flex justify-between text-xs text-slate-500 px-4">
              <div>Issued: <strong className="text-slate-700">{c.issued_date}</strong></div>
              <div>Score: <strong className="text-slate-700">{c.score}%</strong></div>
              <div>ID: <strong className="text-slate-700">{c.certificate_id}</strong></div>
            </div>
            <div className="mt-5 text-[9px] text-slate-400 max-w-lg mx-auto">{c.disclaimer}</div>
          </div>
        </div>
        <div className="flex justify-center gap-2 mt-4 print:hidden">
          <Button className="bg-[#0078d4] text-white" onClick={print} data-testid="cert-verify-print">Print / Save PDF</Button>
          <Link to="/"><Button variant="outline">Home</Button></Link>
        </div>
      </div>
    </div>
  );
}
